import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { toast } from 'react-toastify';
import { getErrorMessage, parseJwt } from '@common/utils';
import { Button, buttonVariants } from '@/components/ui/button';
import TelletLogo from '@/assets/logo/tellet-logo-main.svg';
import EmpathyLogo from '@/assets/logo/empathy-logo-black.svg';
import Illustration from '@/assets/illustrations/accept_org_invite_illustration.svg';
import Image from '@/components/atoms/Image';
import Routes from '@/router/routes';
import { ECompanyThemeName } from '@/enums';
import FullPageLoader from '@/components/atoms/Loader/FullPageLoader';
import { useValidateInvitation } from '@/reactQuery/get';
import {
  useMutateAcceptOrganizationInvitation,
} from '@/reactQuery/post';
import { useAuthentication } from '@/hooks/useAuthentication';

const themeLogosList = {
  [ECompanyThemeName.DEFAULT]: (
    <Image src={TelletLogo} alt="logo" className="absolute w-24 h-fit top-6 left-5" />
  ),
  [ECompanyThemeName.EMPATHY]: (
    <Image src={EmpathyLogo} alt="logo" className="absolute w-44 h-fit top-6 left-5" />
  ),
};

function AcceptOrganizationInvitation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenAccept = searchParams.get('token');
  const { themeName } = useTheme();
  const { organizationId, workspaceId } = useParams();
  const { mutateAsync: validateInvitation } = useValidateInvitation();
  const [isLoading, setIsLoading] = useState(true);
  const { processAuthSuccess } = useAuthentication();

  const parsedJWTToken = parseJwt(tokenAccept || '');
  const tokenExpirationDate = new Date(parsedJWTToken.exp * 1000);
  const hasTokenExpired = tokenExpirationDate < new Date();

  const { mutateAsync: acceptInvitation, isPending } = useMutateAcceptOrganizationInvitation(
    organizationId,
    tokenAccept,
  );

  useEffect(() => {
    const checkInvitationValidity = async () => {
      if (!tokenAccept || hasTokenExpired) {
        navigate(Routes.organization_invitation_link_expired.path);
        return;
      }

      // Add a small delay to show the loader
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 1000);
      });

      // Validate invitation on backend
      try {
        await validateInvitation({
          organizationId: organizationId!,
          token: tokenAccept,
        });
        // Invitation is valid, show the form
        setIsLoading(false);
      } catch (error) {
        const errorMessage = getErrorMessage(error);

        if (errorMessage.toLowerCase().includes('already accepted')) {
          navigate(`${Routes.organization_invitation_link_expired.path}?reason=already_accepted`);
          return;
        }

        // Invitation is cancelled or invalid, redirect to expired page
        navigate(Routes.organization_invitation_link_expired.path);
      }
    };

    checkInvitationValidity();
  }, [tokenAccept, hasTokenExpired, organizationId, navigate, validateInvitation]);

  const onSubmit = async () => {
    try {
      const response = await acceptInvitation();
      const jwt = response?.data?.token;
      const targetPath = `${Routes.organization.path}/${organizationId}${Routes.workspace.path}/${workspaceId}`;
      if (jwt) {
        // Backend returns a JWT on acceptance — set up auth then navigate to the workspace
        await processAuthSuccess(jwt);
        navigate(targetPath, { replace: true });
      } else {
        // No token in response — navigate directly (user may need to log in)
        navigate(targetPath);
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorMessage = getErrorMessage(err);

      if (axiosError?.status === StatusCodes.UNAUTHORIZED) {
        navigate(
          `${
            Routes.verification_email_link_expired.path
          }/${organizationId}?token=${tokenAccept}&inviter=${encodeURIComponent(
            searchParams.get('inviter')!,
          )}`,
        );
      } else if (
        axiosError?.status === StatusCodes.FORBIDDEN ||
        errorMessage.toLowerCase().includes('not found')
      ) {
        navigate(Routes.organization_invitation_link_expired.path);
      } else if (errorMessage.toLowerCase().includes('already accepted')) {
        navigate(`${Routes.organization_invitation_link_expired.path}?reason=already_accepted`);
      } else {
        toast.error(errorMessage || 'Something went wrong. Please try again.');
      }
    }
  };

  if (isLoading) {
    return <FullPageLoader />;
  }

  return (
    <div className="relative flex items-center justify-center min-h-screen px-4 mx-auto text-center">
      {themeLogosList[themeName]}
      <div className="flex flex-col items-center justify-center gap-2 mx-auto text-center">
        <Image
          effect="blur"
          src={Illustration}
          alt="illustration"
          className="min500:w-full w-[450px]"
        />
        <h1 className="text-5xl font-bold mt-14 text-dark min500:text-4xl">Accept invitation</h1>
        <Button
          onClick={onSubmit}
          disabled={isPending}
          className={buttonVariants({
            variant: 'default',
            className: 'mt-10 transition-all ease-in-out hover:!bg-darker-primary',
          })}
        >
          {isPending ? 'Accepting...' : 'Accept invite'}
        </Button>
      </div>
    </div>
  );
}

export default AcceptOrganizationInvitation;
